import React from 'react';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Icons from '../Icons';
import { formatDate, getRiskColor, getRiskLabel, getRiskVariant, isOverdueReview } from './safeguardingUtils';

const statusConfig = {
  open: { label: 'Open', icon: Icons.Alert, variant: 'danger' },
  monitoring: { label: 'Monitoring', icon: Icons.Clock, variant: 'warning' },
  closed: { label: 'Closed', icon: Icons.Check, variant: 'inactive' },
  resolved: { label: 'Closed', icon: Icons.Check, variant: 'inactive' }
};

export default function ActionRequiredList({
  events = [],
  onReviewEvent,
  onAssignOwner,
  onSetReviewDate
}) {
  return (
    <section className="sp-panel sp-action-required p-5" aria-label="Requires immediate action">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>
            Requires Immediate Action
          </h3>
          <p className="text-sm sp-muted">
            Critical and high-risk items that need owner assignment, review dates, or urgent updates.
          </p>
        </div>
        <Badge variant="danger">{events.length} flagged</Badge>
      </div>

      {events.length === 0 ? (
        <div className="text-sm sp-muted mt-4">No safeguarding items require immediate action.</div>
      ) : (
        <div className="mt-4 space-y-2">
          {events.map((event) => {
            const riskLabel = getRiskLabel(event.severity);
            const riskVariant = getRiskVariant(event.severity);
            const statusKey = (event.status || '').toLowerCase();
            const status = statusConfig[statusKey] || statusConfig.open;
            const StatusIcon = status.icon;
            const missingOwner = !event.assignedOwner;
            const missingReview = !event.reviewDate;
            const overdue = isOverdueReview(event);
            return (
              <div
                key={event.id}
                className="sp-panel sp-action-card p-4"
                style={{ borderLeft: `4px solid ${getRiskColor(event.severity)}` }}
              >
                <div className="sp-action-item">
                  <div>
                    <div className="sp-event-kicker">{event.category || 'Safeguarding'}</div>
                    <div className="sp-action-title">
                      {event.type || event.category || 'Safeguarding event'} —{' '}
                      <span className={`sp-risk-text sp-risk-text--${riskVariant}`}>{riskLabel}</span> — {status.label}
                    </div>
                    <div className="sp-action-tags">
                      <Badge variant={riskVariant} className="sp-badge--prominent">{riskLabel}</Badge>
                      <Badge variant={status.variant}>
                        <span aria-hidden="true">
                          <StatusIcon />
                        </span>
                        {status.label}
                      </Badge>
                      {missingOwner && <Badge variant="danger">Missing owner</Badge>}
                      {missingReview && <Badge variant="warning">No review date</Badge>}
                      {overdue && <Badge variant="danger">Overdue review</Badge>}
                    </div>
                    <div className="sp-action-meta">
                      <span>
                        <span className="sp-meta-label">Owner</span>
                        <span className={missingOwner ? 'sp-warning-text' : 'sp-value'}>
                          {event.assignedOwner || 'Unassigned'}
                        </span>
                      </span>
                      <span>
                        <span className="sp-meta-label">Review</span>
                        <span className={missingReview ? 'sp-warning-text' : 'sp-value'}>
                          {formatDate(event.reviewDate) || 'Not scheduled'}
                        </span>
                      </span>
                      <span>
                        <span className="sp-meta-label">Logged</span>
                        <span>{formatDate(event.date) || 'No date'}</span>
                      </span>
                    </div>
                  </div>
                  <div className="sp-actions">
                    <Button
                      size="sm"
                      variant="primary"
                      type="button"
                      onClick={() => onReviewEvent?.(event)}
                      aria-label={`Review safeguarding event ${event.type || event.id}`}
                    >
                      Review / Update
                    </Button>
                    {missingOwner && (
                      <Button
                        size="sm"
                        variant="secondary"
                        type="button"
                        onClick={() => onAssignOwner?.(event)}
                        aria-label={`Assign owner for ${event.type || event.id}`}
                      >
                        Assign owner
                      </Button>
                    )}
                    {missingReview && (
                      <Button
                        size="sm"
                        variant="secondary"
                        type="button"
                        onClick={() => onSetReviewDate?.(event)}
                        aria-label={`Set review date for ${event.type || event.id}`}
                      >
                        Set review date
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
